import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { getApiUrl } from '../../api'

interface ApplicationDetail {
  application_id: number
  program_id: number
  program_name: string
  description: string | null
  target: string | null
  location: string | null
  session_name: string | null
  participation_start_at: string | null
  participation_end_at: string | null
  applied_at: string
  application_status: string
}

export default function ProgramApplicationDetail() {
  const { application_id } = useParams<{ application_id: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const guestName = location.state?.guest_name
  const guestPhone = location.state?.guest_phone

  const [application, setApplication] = useState<ApplicationDetail | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchDetail()
  }, [application_id])

  const fetchDetail = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('access_token')

      if (token) {
        // 회원
        const res = await fetch(getApiUrl(`/marubit/api/program/applications/me/${application_id}`), {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) throw new Error('조회 실패')
        setApplication(await res.json())
      } else if (guestName && guestPhone) {
        // 비회원
        const params = new URLSearchParams({ guest_name: guestName, guest_phone: guestPhone })
        const res = await fetch(getApiUrl(`/marubit/api/program/kiosk/guest/applications/${application_id}?${params}`))
        if (!res.ok) throw new Error('조회 실패')
        setApplication(await res.json())
      } else {
        navigate('/program/check')
      }
    } catch {
      alert('신청 정보를 불러오는 데 실패했습니다.')
      navigate('/program/check')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    })
  }

  const formatDateTime = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    })
  }

  const statusColor = (status: string) => {
    if (status === '신청취소') return 'text-red-400 bg-red-50 border-red-100'
    if (status === '예약완료') return 'text-green-600 bg-green-50 border-green-100'
    if (status === '프로그램종료') return 'text-gray-400 bg-gray-100 border-gray-200'
    return 'text-[#887267] bg-[#f5f2f0] border-[#e8e0dc]'
  }

  const InfoRow = ({ label, value }: { label: string; value: string }) => (
    <div className="flex items-start py-[14px] border-b border-gray-100 last:border-none">
      <span className="w-[100px] text-[13px] text-gray-400 font-medium shrink-0 pt-[1px]">{label}</span>
      <span className="text-[14px] text-[#222] font-medium">{value || '-'}</span>
    </div>
  )

  if (loading) return (
    <div className="min-h-[60vh] flex items-center justify-center text-gray-300">
      불러오는 중...
    </div>
  )

  if (!application) return null

  return (
    <div className="bg-[#fafafa] min-h-screen px-6 py-12">
      <div className="max-w-[1200px] mx-auto">

        {/* 뒤로가기 */}
        <button
          onClick={() => navigate('/program/check')}
          className="mb-6 text-[14px] text-gray-400 hover:text-[#887267] transition-colors flex items-center gap-1"
        >
          ← 신청 목록으로
        </button>

        {/* 타이틀 */}
        <div className="mb-6">
          <h1 className="text-[26px] font-extrabold text-[#5a4035] mb-2">신청 상세</h1>
        </div>

        {/* 신청 상태 */}
        <div className={`flex items-center justify-between px-6 py-4 rounded-2xl border mb-4 ${statusColor(application.application_status)}`}>
          <span className="text-[15px] font-bold">신청 상태</span>
          <span className="text-[15px] font-extrabold">{application.application_status}</span>
        </div>

        {/* 프로그램 정보 */}
        <div className="bg-white rounded-2xl p-7 mb-4 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">
            프로그램 정보
          </h2>
          <InfoRow label="프로그램명" value={application.program_name} />
          {application.description && (
            <InfoRow label="소개" value={application.description} />
          )}
          {application.target && (
            <InfoRow label="대상" value={application.target} />
          )}
          {application.location && (
            <InfoRow label="장소" value={application.location} />
          )}
          {application.session_name && (
            <InfoRow label="회차" value={application.session_name} />
          )}
          <InfoRow
            label="참여일"
            value={`${formatDate(application.participation_start_at)} ~ ${formatDate(application.participation_end_at)}`}
          />
        </div>

        {/* 신청 정보 */}
        <div className="bg-white rounded-2xl p-7 mb-6 f8b62cshadow-[0_2px_12px_rgba(136,114,103,0.06)]">
          <h2 className="text-[16px] font-bold text-[#887267] border-l-[3px] border-[#887267] pl-[10px] mb-4">
            신청 정보
          </h2>
          <InfoRow label="신청일" value={formatDateTime(application.applied_at)} />
          <InfoRow label="신청번호" value={String(application.application_id)} />
        </div>

        {/* 프로그램 상세 보기 버튼 */}
        <button
          onClick={() => navigate(`/program/${application.program_id}`)}
          className="w-full h-[52px] bg-[#887267] text-white border-none rounded-xl text-[16px] font-bold cursor-pointer hover:bg-[#6b5248] transition-colors shadow-[0_4px_16px_rgba(136,114,103,0.25)]"
        >
          프로그램 상세 보기
        </button>
      </div>
    </div>
  )
}